import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { v4 } from "uuid";
import toast from "react-hot-toast";
import { ChevronDown, ExternalLink } from "lucide-react";
import {
  getDocData,
  setDocData,
} from "../../Firebase/Firebase.utils";
import { COLLECTIONS, setPublishStatus } from "../utils/cmsSchema";
import { htmlToPreview } from "../utils/htmlToPreview";
import { resolveUniqueSlug } from "../utils/slugUniqueness";
import { slugify } from "../utils/slugify";
import { publicArticleUrl } from "../utils/publicUrl";
import { revalidateFor } from "../utils/revalidatePublic";
import FormShell, {
  FormSection,
  PublishToggle,
  TextArea,
  TextField,
} from "../components/FormShell";
import RichTextEditor from "../components/RichTextEditor";
import FileDropzone from "../components/FileDropzone";

const schema = COLLECTIONS.insights;

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function InsightForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);

  const isNew = !id;
  const docId = useMemo(() => id || v4(), [id]);

  const [form, setForm] = useState({
    title: "",
    date: todayIso(),
    content: "",
    image: "",
    pdf: "",
    slug: "",
    metaTitle: "",
    metaDescription: "",
    published: false,
  });
  const [originalDoc, setOriginalDoc] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [seoOpen, setSeoOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const trackUpload = (b) =>
    setUploadingCount((c) => Math.max(0, c + (b ? 1 : -1)));

  useEffect(() => {
    if (isNew) return;
    let active = true;
    (async () => {
      const data = await getDocData(schema.key, id);
      if (!active) return;
      if (!data) {
        toast.error("Not found.");
        navigate(schema.route);
        return;
      }
      setOriginalDoc(data);
      setForm({
        title: data.title || "",
        date: data.date ? String(data.date).slice(0, 10) : todayIso(),
        content:
          data.content ||
          (data.description ? `<p>${data.description}</p>` : ""),
        image: data.image || "",
        pdf: data.pdf || "",
        slug: data.slug || "",
        metaTitle: data.metaTitle || "",
        metaDescription: data.metaDescription || "",
        published: data.published === true,
      });
      setSlugTouched(!!data.slug);
      setIsLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id, isNew, navigate]);

  // Auto-derive slug from title when the user hasn't touched the slug
  // field. Matches the blogs/news pattern.
  useEffect(() => {
    if (slugTouched) return;
    if (!form.title) return;
    setForm((p) => ({ ...p, slug: slugify(form.title) }));
  }, [form.title, slugTouched]);

  const handleSave = async (publishOverride) => {
    if (!form.title.trim()) {
      toast.error("Heading is required.");
      return;
    }
    if (uploadingCount > 0) {
      toast.error("Wait for uploads to finish.");
      return;
    }
    setIsSaving(true);
    try {
      const finalSlug = await resolveUniqueSlug({
        collectionName: schema.key,
        desiredSlug: form.slug,
        fallbackTitle: form.title,
        ignoreId: docId,
      });
      const nowIso = new Date().toISOString();
      const published =
        typeof publishOverride === "boolean"
          ? publishOverride
          : form.published;
      const trimmedTitle = form.title.trim();
      const payload = {
        ...(originalDoc || {}),
        title: trimmedTitle,
        titleLower: trimmedTitle.toLowerCase(),
        date: form.date,
        content: form.content || "",
        description: htmlToPreview(form.content || "", Infinity),
        image: form.image || "",
        pdf: form.pdf || "",
        slug: finalSlug,
        metaTitle: form.metaTitle.trim(),
        metaDescription: form.metaDescription.trim(),
        updatedAt: nowIso,
        lastUpdatedBy: currentUser?.email || null,
      };
      setPublishStatus(payload, schema, published);
      if (isNew) {
        payload.createdAt = nowIso;
        payload.createdBy = currentUser?.email || null;
      }
      await setDocData(schema.key, docId, payload);
      revalidateFor(schema.key);
      toast.success(
        published
          ? `${schema.singular} published.`
          : `${schema.singular} saved.`
      );
      setForm((p) => ({ ...p, slug: finalSlug, published }));
      if (isNew) {
        navigate(`${schema.route}/${docId}`, { replace: true });
      } else {
        setOriginalDoc(payload);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }

  const saveDisabled = isSaving || uploadingCount > 0;

  return (
    <FormShell
      backTo={schema.route}
      backLabel="Back to Market Insights"
      eyebrow={isNew ? "New Insight" : "Editing Insight"}
      title={form.title || (isNew ? "Untitled Insight" : "Untitled")}
      belowTitle={
        !isNew ? (
          <a
            href={publicArticleUrl(schema.key, {
              slug: form.slug,
              id: docId,
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-leechiuBlue hover:underline"
          >
            View public page
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : null
      }
      footer={
        <>
          <button
            type="button"
            onClick={() => navigate(schema.route)}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {form.published ? "Update & Keep Published" : "Publish"}
          </button>
        </>
      }
    >
      <div className="lg:col-span-2 space-y-6">
        <FormSection
          title="Details"
          description="The headline shown on /market-insights and the publication date."
        >
          <TextField
            label="Heading"
            required
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="e.g. Q4 2025 Office Market Outlook"
          />
          <TextField
            label="Date"
            type="date"
            required
            value={form.date}
            onChange={(e) => setField("date", e.target.value)}
            className="max-w-xs"
          />
        </FormSection>

        <FormSection
          title="Description"
          description="Rich content — supports inline images, lists, and links. The plain-text version is shown on the report card."
        >
          <RichTextEditor
            value={form.content}
            onChange={(html) => setField("content", html)}
            collection={schema.key}
            docId={docId}
            placeholder="Summarize the report and add highlights, charts, or key findings…"
            onUploadingChange={trackUpload}
          />
        </FormSection>

        <FormSection
          title={
            <button
              type="button"
              onClick={() => setSeoOpen((s) => !s)}
              className="flex items-center gap-1 text-sm font-semibold text-leechiuBlue"
            >
              SEO &amp; URL
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  seoOpen ? "" : "-rotate-90"
                }`}
              />
            </button>
          }
          description="Used by search engines and social previews. The slug drives the public /market-insights/<slug> URL."
        >
          {seoOpen ? (
            <>
              <TextField
                label="Insight URL"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField("slug", e.target.value);
                }}
                onBlur={(e) => {
                  const formatted = slugify(e.target.value);
                  if (formatted !== e.target.value) {
                    setField("slug", formatted);
                  }
                }}
                placeholder="auto-generated-from-title"
                hint="Paste any word or phrase — it will be formatted to a URL-friendly slug when you click away. Leave blank to auto-generate from the title. Uniqueness is checked on save."
              />
              <TextField
                label="Page Title"
                value={form.metaTitle}
                onChange={(e) => setField("metaTitle", e.target.value)}
                placeholder="Shown in browser tab and search results"
                hint="Recommended: 50–60 characters"
              />
              <TextArea
                label="Meta Description"
                rows={3}
                value={form.metaDescription}
                onChange={(e) => setField("metaDescription", e.target.value)}
                placeholder="A short summary for search results and social shares"
                hint="Recommended: 140–160 characters"
              />
            </>
          ) : null}
        </FormSection>
      </div>

      <div className="space-y-6">
        <FormSection
          title="Report Cover"
          description="Shown on the /market-insights listing card and inside the download CTA on the detail page."
        >
          <FileDropzone
            kind="image"
            collection={schema.key}
            docId={docId}
            value={form.image}
            onChange={(url) => setField("image", url)}
            onUploadingChange={trackUpload}
            required
          />
        </FormSection>

        <FormSection
          title="PDF Report"
          description="The downloadable file emailed to inquirers."
        >
          <FileDropzone
            kind="pdf"
            collection={schema.key}
            docId={docId}
            value={form.pdf}
            onChange={(url) => setField("pdf", url)}
            onUploadingChange={trackUpload}
            required
          />
        </FormSection>

        <FormSection title="Visibility">
          <PublishToggle
            value={form.published}
            onChange={(v) => setField("published", v)}
          />
        </FormSection>
      </div>
    </FormShell>
  );
}
